import { create } from 'zustand';
import { api, codebasesApi, githubApi } from '../services/api';

export interface Codebase {
  id: string;
  name: string;
  description: string;
  fileCount: number;
  rootPath: string;
  createdAt: string;
  files?: Array<{ path: string; type: string; size: number; extension: string }>;
}

export interface GitRepo {
  name: string;
  path: string;
  branch: string;
  remoteUrl: string;
  lastModified: string;
}

export type ContextMode = 'standard' | 'codebase' | 'git';

export interface CodeAnalysis {
  issues: CodeIssue[];
  suggestions: CodeSuggestion[];
  complexity: ComplexityMetrics;
  security: SecurityIssue[];
}

export interface CodeIssue {
  line: number;
  column: number;
  severity: 'error' | 'warning' | 'info';
  message: string;
  rule: string;
  fix?: string;
}

export interface CodeSuggestion {
  line: number;
  column: number;
  message: string;
  type: 'performance' | 'readability' | 'best-practice';
  original: string;
  suggested: string;
}

export interface ComplexityMetrics {
  cyclomatic: number;
  cognitive: number;
  linesOfCode: number;
  maintainability: number;
}

export interface SecurityIssue {
  line: number;
  severity: 'critical' | 'high' | 'medium' | 'low';
  message: string;
  cwe: string;
}

export interface InlineCompletion {
  id: string;
  text: string;
  detail: string;
  range: {
    startLine: number;
    startColumn: number;
    endLine: number;
    endColumn: number;
  };
  confidence: number;
}

export interface Dependency {
  from: string;
  to: string;
  type: 'import' | 'require' | 'extends' | 'implements';
}

interface CodeEditorState {
  // Editor content
  code: string;
  language: string;
  fileName: string;
  
  // Context mode
  contextMode: ContextMode;
  selectedCodebase: Codebase | null;
  selectedGitRepo: GitRepo | null;
  codebases: Codebase[];
  gitRepos: GitRepo[];
  isLoadingContext: boolean;
  
  // Analysis results
  analysis: CodeAnalysis | null;
  dependencies: Dependency[];
  isAnalyzing: boolean;
  
  // Inline completions
  completions: InlineCompletion[];
  showCompletions: boolean;
  
  // UI state
  activePanel: 'analysis' | 'dependencies' | 'duplicates' | 'none';
  selectedIssue: CodeIssue | null;
  
  // Actions
  setCode: (code: string) => void;
  setLanguage: (language: string) => void;
  setFileName: (fileName: string) => void;
  setContextMode: (mode: ContextMode) => void;
  setSelectedCodebase: (codebase: Codebase | null) => void;
  setSelectedGitRepo: (repo: GitRepo | null) => void;
  loadCodebases: () => Promise<void>;
  loadGitRepos: () => Promise<void>;
  loadFileFromCodebase: (codebaseId: string, filePath: string) => Promise<void>;
  loadFileFromGit: (repoPath: string, filePath: string) => Promise<void>;
  analyzeCode: () => Promise<void>;
  getCompletions: (cursorLine: number, cursorColumn: number) => Promise<void>;
  setCompletions: (completions: InlineCompletion[]) => void;
  clearCompletions: () => void;
  togglePanel: (panel: 'analysis' | 'dependencies' | 'duplicates' | 'none') => void;
  selectIssue: (issue: CodeIssue | null) => void;
  reset: () => void;
}

const initialState = {
  code: '',
  language: 'typescript',
  fileName: 'untitled.ts',
  analysis: null,
  dependencies: [],
  isAnalyzing: false,
  completions: [],
  showCompletions: false,
  activePanel: 'analysis' as const,
  selectedIssue: null,
  contextMode: 'standard' as ContextMode,
  selectedCodebase: null,
  selectedGitRepo: null,
  codebases: [],
  gitRepos: [],
  isLoadingContext: false,
};

export const useCodeEditorStore = create<CodeEditorState>((set, get) => ({
  ...initialState,

  setCode: (code) => set({ code }),
  
  setLanguage: (language) => set({ language }),
  
  setFileName: (fileName) => set({ fileName }),
  
  setContextMode: (mode) => set({ contextMode: mode, selectedCodebase: null, selectedGitRepo: null }),
  
  setSelectedCodebase: (codebase) => set({ selectedCodebase: codebase }),
  
  setSelectedGitRepo: (repo) => set({ selectedGitRepo: repo }),
  
  loadCodebases: async () => {
    try {
      const response = await codebasesApi.list();
      // Handle different response formats
      const data = (response.data as any)?.data || response.data || [];
      set({ codebases: Array.isArray(data) ? data : [] });
    } catch (error) {
      console.error('Failed to load codebases:', error);
      set({ codebases: [] });
    }
  },
  
  loadGitRepos: async () => {
    try {
      const response = await githubApi.getClonedRepos();
      // Handle different response formats - githubApi returns data directly
      const data = Array.isArray((response as any).data) ? (response as any).data : [];
      set({ gitRepos: data });
    } catch (error) {
      console.error('Failed to load git repos:', error);
      set({ gitRepos: [] });
    }
  },
  
  loadFileFromCodebase: async (codebaseId: string, filePath: string) => {
    set({ isLoadingContext: true });
    try {
      const response = await api.get(`/codebases/${codebaseId}/file?path=${encodeURIComponent(filePath)}`);
      const content = response.data?.content || '';
      const detectedLanguage = response.data?.language || 'typescript';
      set({ 
        code: content, 
        language: detectedLanguage,
        fileName: filePath.split('/').pop() || filePath,
        isLoadingContext: false 
      });
    } catch (error) {
      console.error('Failed to load file from codebase:', error);
      set({ isLoadingContext: false });
    }
  },
  
  loadFileFromGit: async (repoPath: string, filePath: string) => {
    set({ isLoadingContext: true });
    try {
      const response = await api.get(`/git/file/content?repoPath=${encodeURIComponent(repoPath)}&filePath=${encodeURIComponent(filePath)}`);
      const content = response.data?.content || '';
      // Extract language from file extension
      const ext = filePath.split('.').pop() || 'typescript';
      set({ 
        code: content, 
        language: ext,
        fileName: filePath.split('/').pop() || filePath,
        isLoadingContext: false 
      });
    } catch (error) {
      console.error('Failed to load file from git repo:', error);
      set({ isLoadingContext: false });
    }
  },
  
  analyzeCode: async () => {
    const { code, language } = get();
    if (!code.trim()) return;
    
    set({ isAnalyzing: true });
    
    try {
      const response = await api.post('/code-intelligence/full-analysis', {
        code,
        language
      });
      const data = response.data;
      
      set({ 
        analysis: data,
        dependencies: data.dependencies || [],
        isAnalyzing: false 
      });
    } catch (error) {
      console.error('Code analysis failed:', error);
      set({ isAnalyzing: false });
    }
  },

  getCompletions: async (cursorLine, cursorColumn) => {
    const { code, language } = get();
    if (!code.trim()) return;
    
    try {
      const response = await api.post('/code-intelligence/completions', {
        code,
        cursorLine,
        cursorColumn,
        language
      });
      const completions = response.data;
      
      set({ 
        completions, 
        showCompletions: completions.length > 0 
      });
    } catch (error) {
      console.error('Get completions failed:', error);
    }
  },

  setCompletions: (completions) => set({ completions }),
  
  clearCompletions: () => set({ completions: [], showCompletions: false }),
  
  togglePanel: (panel) => set((state) => ({
    activePanel: state.activePanel === panel ? 'none' : panel
  })),
  
  selectIssue: (issue) => set({ selectedIssue: issue }),
  
  reset: () => set(initialState),
}));
